import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import moment from 'moment';
import 'moment/locale/th';
import shrinkImage from './shrinkImage';
import { setUrlParams } from './cleanerToken';
import {
  stockRequest, newActionId, INCIDENT_TYPES, RESPONSIBLE, STATUS_COLOR, money, errText, lsGet, lsSet, lsDel,
} from './stockApi';

// ระบบคลังสินค้า -- หน้าแม่บ้าน (/once/cleaner?view=stock[&tab=report|reported|mine|room][&incident=<id>])
//   แจ้งของเสีย/หาย · ที่ฉันแจ้ง · รายการหักของฉัน (ยอมรับ/โต้แย้ง) · ของในห้อง (เติมให้ครบ)
// ลิงก์จากไลน์: ?view=stock&incident=<id>&t=<token> -> เปิดรายการนั้นเลย
// หักเงินแม่บ้านได้เฉพาะเมื่อแม่บ้านกดยอมรับเอง (พ.ร.บ.คุ้มครองแรงงาน ม.76 -- ดู docs/inventory-system-plan.md ข้อ 3)
const MAX_PHOTOS = 5;
const DRAFT_KEY = 'once_stock_report_draft';
const EMPTY_FORM = { room_id: '', item_id: null, item_label: '', custom: false, name: '', qty: '1', incident_type: '', responsible: '', note: '', photos: [], client_action_id: null };
const TABS = [['report', 'แจ้งของเสีย/หาย'], ['reported', 'ที่ฉันแจ้ง'], ['mine', 'รายการหักของฉัน'], ['room', 'ของในห้อง']];

const Req = () => <span style={{ color: '#CD3E33' }}> *</span>;
const FieldErr = ({ msg }) => (msg ? <div style={{ color: '#CD3E33', fontSize: 14, fontWeight: 700, marginTop: 6 }}>{msg}</div> : null);

// ลายเซ็น (ไม่บังคับ) -- วาดด้วยนิ้ว ส่งเป็น PNG ผ่าน upload-photo เดิม
function SignaturePad({ padRef, onInk }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(c.offsetWidth * dpr);
    c.height = Math.round(c.offsetHeight * dpr);
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#1A211D';
    padRef.current = {
      clear: () => { ctx.clearRect(0, 0, c.width, c.height); onInk(false); },
      toBlob: () => new Promise((resolve) => c.toBlob((b) => resolve(b), 'image/png')),
    };
    // eslint-disable-next-line
  }, []);
  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const [x, y] = pos(e);
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + 0.1, y + 0.1); ctx.stroke();
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    onInk(true);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const [x, y] = pos(e);
    ctx.lineTo(x, y); ctx.stroke();
  };
  const up = () => { drawing.current = false; };
  return (
    <canvas ref={canvasRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} onPointerCancel={up}
      style={{ display: 'block', width: '100%', height: 160, border: '1.5px dashed #CBD4CC', borderRadius: 12, background: '#FAFBFA', touchAction: 'none' }} />
  );
}

export default function StockWork({ token, cw, S, onBack }) {
  const params = new URLSearchParams(window.location.search);
  const initialIncident = params.get('incident');
  const initialTab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'report';
  const st = useMemo(() => stockRequest(token), [token]);

  const [sme, setSme] = useState(null);        // { enabled, pending_ack, deduction_cap_pct }
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [list, setList] = useState(null);
  const [listError, setListError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(0);

  // ฟอร์มแจ้ง (เก็บร่างในเครื่องจนส่งสำเร็จ)
  const [f, setF] = useState(() => ({ ...EMPTY_FORM, ...(lsGet(DRAFT_KEY) || {}) }));
  const [errors, setErrors] = useState({});
  const [items, setItems] = useState(null);
  const [itemQ, setItemQ] = useState('');
  const [created, setCreated] = useState(null);
  const itemSeq = useRef(0);

  // รายละเอียด: ยอมรับ / โต้แย้ง
  const [ackOpen, setAckOpen] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [ackError, setAckError] = useState(null);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeNote, setDisputeNote] = useState('');
  const [disputeErr, setDisputeErr] = useState({});
  const padRef = useRef(null);
  const actionIds = useRef({});

  // ของในห้อง
  const [parRoom, setParRoom] = useState('');
  const [parRows, setParRows] = useState(null);
  const [fillResult, setFillResult] = useState(null);
  const [parError, setParError] = useState(null);

  const loadMe = useCallback(() => st('me').then((r) => { setSme(r.data); return r.data; }), [st]);

  const loadList = useCallback((scope) => {
    setList(null); setListError(null);
    st(`incidents?scope=${scope}`).then((r) => setList(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { setList([]); setListError(errText(e)); });
  }, [st]);

  const openIncident = useCallback((id) => {
    setAckOpen(false); setDisputeOpen(false); setAckError(null); setDisputeErr({}); setDisputeNote('');
    st(`incidents/${id}`).then((r) => { setDetail(r.data); setUrlParams({ incident: id }); })
      .catch((e) => { setUrlParams({ incident: null }); alert(errText(e)); });
  }, [st]);

  useEffect(() => {
    moment.locale('th');
    loadMe().then((m) => {
      if (initialIncident) openIncident(initialIncident);
      if (!m.enabled) return;
      st('rooms').then((r) => setRooms(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      if (['reported', 'mine'].includes(initialTab)) loadList(initialTab);
    }).catch((e) => setError(e?.response?.status === 401 ? 'ลิงก์ไม่ถูกต้องหรือหมดอายุ กรุณาเปิดลิงก์ส่วนตัวจากไลน์อีกครั้ง' : errText(e)));
    // eslint-disable-next-line
  }, []);

  // เก็บร่างฟอร์มแจ้ง
  useEffect(() => {
    const empty = !f.room_id && !f.item_id && !f.name && !f.incident_type && !f.responsible && !f.note && !f.photos.length && String(f.qty) === '1';
    if (empty) lsDel(DRAFT_KEY); else lsSet(DRAFT_KEY, f);
  }, [f]);

  // รายการของ: ของประจำห้องก่อน (in_room) แล้วของอื่น · ค้นหา ?q=
  useEffect(() => {
    if (tab !== 'report' || !sme?.enabled || f.item_id || f.custom) return undefined;
    const seq = ++itemSeq.current;
    const h = setTimeout(() => {
      const qs = new URLSearchParams();
      if (f.room_id) qs.set('room_id', f.room_id);
      if (itemQ.trim()) qs.set('q', itemQ.trim());
      st(`items?${qs.toString()}`).then((r) => { if (seq === itemSeq.current) setItems(Array.isArray(r.data) ? r.data : []); })
        .catch(() => { if (seq === itemSeq.current) setItems([]); });
    }, itemQ ? 350 : 0);
    return () => clearTimeout(h);
  }, [tab, sme, f.room_id, f.item_id, f.custom, itemQ, st]);

  const switchTab = (k) => {
    setTab(k); setDetail(null); setCreated(null); setUrlParams({ tab: k, incident: null });
    if (k === 'reported' || k === 'mine') loadList(k);
    if (k === 'mine') loadMe().catch(() => {});
  };
  const backToList = () => {
    setDetail(null); setAckOpen(false); setDisputeOpen(false); setUrlParams({ incident: null });
    if (tab === 'reported' || tab === 'mine') loadList(tab);
  };

  const editF = (patch) => {
    setF((x) => ({ ...x, ...patch }));
    setErrors((e) => { const n = { ...e }; Object.keys(patch).forEach((k) => { delete n[k]; }); if ('custom' in patch || 'name' in patch) delete n.item_id; delete n.submit; return n; });
  };

  // ---------- รูป ----------
  const uploadFile = async (body, name) => {
    const fd = new FormData(); fd.append('file', body, name);
    const r = await cw('upload-photo', { method: 'post', data: fd, timeout: 90000 });
    return r.data.url;
  };
  const onPickPhotos = async (files) => {
    const picked = Array.from(files || []).slice(0, MAX_PHOTOS - f.photos.length);
    for (const file of picked) { // eslint-disable-line no-restricted-syntax
      setUploading((n) => n + 1);
      try {
        const url = await uploadFile(await shrinkImage(file), 'stock.jpg'); // eslint-disable-line no-await-in-loop
        setF((x) => ({ ...x, photos: [...x.photos, url].slice(0, MAX_PHOTOS) }));
        setErrors((e) => ({ ...e, photos: undefined }));
      } catch (e) { setErrors((er) => ({ ...er, photos: 'ส่งรูปไม่สำเร็จบางรูป — ลองใหม่อีกครั้ง' })); }
      setUploading((n) => Math.max(0, n - 1));
    }
  };

  // ---------- ส่งแจ้ง ----------
  const validate = () => {
    const e = {};
    if (!f.room_id) e.room_id = 'กรุณาเลือกห้อง';
    if (f.custom ? !f.name.trim() : !f.item_id) e.item_id = f.custom ? 'กรุณาพิมพ์ชื่อของ' : 'กรุณาเลือกของ หรือกด "ของไม่มีในรายการ"';
    if (!(Number(f.qty) > 0)) e.qty = 'จำนวนต้องมากกว่า 0';
    if (!f.incident_type) e.incident_type = 'กรุณาเลือกว่าหาย/แตก/เปื้อน/เสื่อม';
    if (!f.responsible) e.responsible = 'กรุณาเลือกว่าเกิดจากใคร';
    if (uploading) e.photos = 'รอรูปส่งเสร็จก่อนนะคะ';
    return e;
  };
  const submitReport = () => {
    if (busy) return;
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    const actionId = f.client_action_id || newActionId(); // ส่งซ้ำ (เน็ตหลุด) ใช้ id เดิม -- เก็บในร่างด้วย
    if (!f.client_action_id) setF((x) => ({ ...x, client_action_id: actionId }));
    setBusy(true);
    st('incidents', {
      method: 'post',
      timeout: 60000,
      data: {
        room_id: f.room_id,
        ...(f.custom ? { name: f.name.trim() } : { item_id: f.item_id }),
        qty: Number(f.qty),
        incident_type: f.incident_type,
        responsible: f.responsible,
        photos: f.photos,
        note: f.note.trim() || null,
        client_action_id: actionId,
        occurred_at: new Date().toISOString(),
      },
    }).then((r) => {
      setBusy(false);
      lsDel(DRAFT_KEY);
      setF({ ...EMPTY_FORM, room_id: f.room_id }); // ห้องเดิม แจ้งต่อได้เลย
      setItemQ(''); setErrors({});
      setCreated(r.data);
    }).catch((err) => {
      setBusy(false);
      // server ตอบแล้ว (ไม่ใช่เน็ตหลุด) -> ครั้งหน้าใช้ id ใหม่
      if (err?.response) setF((x) => ({ ...x, client_action_id: null }));
      const fe = err?.response?.data?.errors;
      if (fe && typeof fe === 'object') {
        const mapped = { ...fe };
        if (mapped.name && !mapped.item_id) mapped.item_id = mapped.name;
        setErrors(mapped);
      } else setErrors({ submit: errText(err) });
    });
  };

  // ---------- ยอมรับ / โต้แย้ง ----------
  const actKey = (id, a) => `${id}:${a}`;
  const takeId = (key) => { if (!actionIds.current[key]) actionIds.current[key] = newActionId(); return actionIds.current[key]; };

  const acknowledge = async () => {
    if (busy) return;
    setBusy(true); setAckError(null);
    const key = actKey(detail.id, 'ack');
    try {
      let signatureUrl = null;
      if (hasInk && padRef.current) {
        const blob = await padRef.current.toBlob();
        if (blob) {
          try { signatureUrl = await uploadFile(blob, 'signature.png'); } catch (e) { throw new Error('ส่งลายเซ็นไม่สำเร็จ — ลองอีกครั้ง หรือกด "ล้าง" แล้วยืนยันโดยไม่เซ็น'); }
        }
      }
      const r = await st(`incidents/${detail.id}/acknowledge`, {
        method: 'post', timeout: 60000,
        data: { signature_url: signatureUrl, client_action_id: takeId(key), occurred_at: new Date().toISOString() },
      });
      delete actionIds.current[key];
      setBusy(false); setAckOpen(false); setHasInk(false);
      setDetail(r.data);
      loadMe().catch(() => {});
    } catch (e) {
      if (e?.response) delete actionIds.current[key];
      setBusy(false);
      setAckError(e?.isAxiosError ? errText(e) : (e?.message || errText(e)));
    }
  };

  const dispute = () => {
    if (busy) return;
    if (!disputeNote.trim()) { setDisputeErr({ note: 'กรุณาบอกเหตุผลที่ไม่เห็นด้วย' }); return; }
    const key = actKey(detail.id, 'dispute');
    setBusy(true); setDisputeErr({});
    st(`incidents/${detail.id}/dispute`, {
      method: 'post', timeout: 60000,
      data: { note: disputeNote.trim(), client_action_id: takeId(key), occurred_at: new Date().toISOString() },
    }).then((r) => {
      delete actionIds.current[key];
      setBusy(false); setDisputeOpen(false); setDisputeNote('');
      setDetail(r.data);
      loadMe().catch(() => {});
    }).catch((e) => {
      if (e?.response) delete actionIds.current[key];
      setBusy(false);
      const fe = e?.response?.data?.errors;
      setDisputeErr(fe?.note ? { note: fe.note } : { submit: errText(e) });
    });
  };

  // ---------- ของในห้อง ----------
  const loadPars = (roomId) => {
    setParRoom(roomId); setParRows(null); setFillResult(null); setParError(null);
    if (!roomId) return;
    st(`items?room_id=${roomId}`).then((r) => setParRows((Array.isArray(r.data) ? r.data : []).filter((x) => x.in_room)))
      .catch((e) => { setParRows([]); setParError(errText(e)); });
  };
  const fillRoom = () => {
    if (busy || !parRoom) return;
    const key = `fill:${parRoom}`;
    setBusy(true); setParError(null);
    st(`rooms/${parRoom}/fill`, { method: 'post', timeout: 60000, data: { client_action_id: takeId(key), occurred_at: new Date().toISOString() } })
      .then((r) => {
        delete actionIds.current[key];
        setBusy(false);
        setFillResult(Array.isArray(r.data?.filled) ? r.data.filled : []);
        setParRows((Array.isArray(r.data?.rows) ? r.data.rows : []));
      })
      .catch((e) => { if (e?.response) delete actionIds.current[key]; setBusy(false); setParError(errText(e)); });
  };

  // ---------- styles ----------
  const input = { boxSizing: 'border-box', width: '100%', border: '1.5px solid #CBD4CC', borderRadius: 10, padding: 12, fontSize: 16, background: '#fff' };
  const errBorder = (k) => (errors[k] ? { borderColor: '#CD3E33' } : null);
  const chip = (on, color = '#0F8A56') => ({ border: `1.5px solid ${on ? color : '#CBD4CC'}`, borderRadius: 12, padding: '12px 14px', fontSize: 16, fontWeight: 800, minHeight: 48, background: on ? color : '#fff', color: on ? '#fff' : '#3B443D', flex: '1 1 auto' });
  const label = { fontWeight: 800, marginBottom: 8 };
  const thumb = (url, size = 56) => (url
    ? <img src={url} alt="" style={{ width: size, height: size, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
    : <div style={{ width: size, height: size, borderRadius: 8, background: '#EEF2EF', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size / 2.4 }}>📦</div>);
  const pill = (i) => <span style={S.pill(STATUS_COLOR[i.status] || '#8A938B')}>{i.status_label || i.status}</span>;
  const header = (title, sub, back, backLabel = '‹ กลับ') => (
    <div style={S.bar}>
      {back && <button style={S.back} onClick={back}>{backLabel}</button>}
      <div style={{ minWidth: 0 }}><h1 style={{ ...S.h, fontSize: 18 }}>{title}</h1>{sub && <div style={S.sub}>{sub}</div>}</div>
    </div>
  );

  if (error) {
    return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 40 }}>🔒</div><p style={{ marginTop: 12, color: '#586159' }}>{error}</p>
      {onBack && <button style={{ ...S.back, marginTop: 12 }} onClick={onBack}>‹ กลับหน้าหลัก</button>}
    </div>;
  }
  if (!sme) return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80, color: '#586159' }}>กำลังโหลด…</div>;

  // ================= DETAIL =================
  if (detail) {
    const d = detail;
    const charge = Number(d.charge_amount) || 0;
    const deducted = Number(d.deducted) || 0;
    const canAnswer = d.is_mine && d.status === 'awaiting_ack';
    const canAckAgain = d.is_mine && d.status === 'disputed';
    const showProgress = d.is_mine && ['pending_charge', 'charged'].includes(d.status) && charge > 0;
    const cap = sme.deduction_cap_pct;
    return (
      <div style={S.page}>
        {header(`${d.incident_no || 'รายการ'} · ${d.name}`, d.room, backToList, '‹ รายการ')}

        <div style={S.card}>
          <div style={{ display: 'flex', gap: 12 }}>
            {thumb(d.photo_url, 72)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{d.name} × {Number(d.qty)} {d.unit}</div>
              <div style={{ fontSize: 14, color: '#586159', marginTop: 2 }}>{d.type_label}{d.room ? ` · ${d.room}` : ''}</div>
              <div style={{ fontSize: 13, color: '#586159' }}>{d.occurred_at ? moment(d.occurred_at).format('D MMM YY HH:mm') : ''}{d.responsible_label ? ` · ผู้รับผิดชอบ: ${d.responsible_label}` : ''}</div>
              <div style={{ marginTop: 6 }}>{pill(d)}</div>
            </div>
          </div>
          {d.note && <div style={{ marginTop: 10, whiteSpace: 'pre-wrap', fontSize: 15 }}>📝 {d.note}</div>}
          {(d.photos || []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              {d.photos.map((u) => <a key={u} href={u} target="_blank" rel="noreferrer"><img src={u} alt="" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 10 }} /></a>)}
            </div>
          )}
        </div>

        {d.is_mine && (d.negligence_label || charge > 0) && (
          <div style={{ ...S.card, borderColor: canAnswer ? '#CD3E33' : '#DBE2DC' }}>
            {d.negligence_label && <div style={{ fontSize: 15 }}>ระดับความผิด: <b>{d.negligence_label}</b></div>}
            {charge > 0 && (
              <div style={{ display: 'flex', alignItems: 'baseline', marginTop: 6 }}>
                <div style={{ flex: 1, fontWeight: 800 }}>ยอดที่ขอหัก</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#CD3E33' }}>฿{money(charge)}</div>
              </div>
            )}
            {d.charge_note && <div style={{ fontSize: 14, color: '#586159', marginTop: 4, whiteSpace: 'pre-wrap' }}>{d.charge_note}</div>}
            {d.staff_acknowledged_at && <div style={{ fontSize: 13, color: '#0F8A56', marginTop: 6, fontWeight: 700 }}>✓ คุณยอมรับเมื่อ {moment(d.staff_acknowledged_at).format('D MMM YY HH:mm')}</div>}
            {d.status === 'disputed' && d.staff_dispute_note && (
              <div style={{ marginTop: 8, padding: 10, borderRadius: 10, background: '#F3EEFA', fontSize: 14 }}>
                <b>คุณโต้แย้งว่า:</b> {d.staff_dispute_note}<div style={{ color: '#586159', marginTop: 4 }}>รอแอดมินพิจารณา — ยังไม่มีการหักเงิน</div>
              </div>
            )}
          </div>
        )}

        {showProgress && (
          <div style={S.card}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>การหักเงิน</div>
            <div style={{ height: 12, borderRadius: 99, background: '#EEF2EF', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, (deducted / charge) * 100)}%`, height: '100%', background: '#0F8A56' }} />
            </div>
            <div style={{ display: 'flex', fontSize: 14, marginTop: 6 }}>
              <div style={{ flex: 1 }}>หักแล้ว ฿{money(deducted)}</div>
              <div>เหลือ ฿{money(Math.max(0, charge - deducted))}</div>
            </div>
            {Number(d.charged_amount) > 0 && <div style={{ fontSize: 13, color: '#586159', marginTop: 4 }}>เก็บแล้วทั้งหมด ฿{money(d.charged_amount)}</div>}
            {d.status === 'pending_charge' && <div style={{ fontSize: 13, color: '#586159', marginTop: 4 }}>หักจากใบจ่ายเงินรอบถัดไป{cap ? ` ไม่เกิน ${cap}% ต่อใบ` : ''} ส่วนที่เหลือยกไปใบถัดไป</div>}
          </div>
        )}

        {(canAnswer || canAckAgain) && !ackOpen && !disputeOpen && (
          <>
            <button disabled={busy} style={S.bigBtn('#0F8A56')} onClick={() => { setAckOpen(true); setAckError(null); setHasInk(false); }}>ยอมรับยอดนี้</button>
            {canAnswer && (
              <button disabled={busy} style={{ ...S.back, width: '100%', marginTop: 10, padding: 16, fontSize: 17, color: '#7A4FB0' }} onClick={() => { setDisputeOpen(true); setDisputeErr({}); }}>ไม่เห็นด้วย / โต้แย้ง</button>
            )}
          </>
        )}

        {disputeOpen && (
          <div style={S.card}>
            <div style={label}>เหตุผลที่ไม่เห็นด้วย<Req /></div>
            <textarea rows={4} value={disputeNote} onChange={(e) => { setDisputeNote(e.target.value); setDisputeErr({}); }}
              placeholder="เช่น ของเสียอยู่ก่อนแล้ว / ลูกค้าเป็นคนทำ / ใช้งานตามปกติ" style={{ ...input, ...(disputeErr.note ? { borderColor: '#CD3E33' } : null) }} />
            <FieldErr msg={disputeErr.note} />
            <div style={{ fontSize: 13, color: '#586159', marginTop: 6 }}>แอดมินจะพิจารณาอีกครั้ง ระหว่างนี้จะไม่มีการหักเงิน</div>
            <FieldErr msg={disputeErr.submit} />
            <button disabled={busy} style={S.bigBtn('#7A4FB0')} onClick={dispute}>{busy ? 'กำลังส่ง…' : 'ส่งเหตุผล'}</button>
            <button style={{ ...S.back, width: '100%', marginTop: 10 }} onClick={() => setDisputeOpen(false)}>ยกเลิก</button>
          </div>
        )}

        {ackOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
            <div style={{ boxSizing: 'border-box', width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '18px 18px 0 0', padding: 20, paddingBottom: 32 }}>
              <div style={{ fontSize: 19, fontWeight: 800 }}>ยืนยันยอมรับยอดหัก ฿{money(charge)}?</div>
              <div style={{ fontSize: 14, color: '#586159', marginTop: 4 }}>{d.name} × {Number(d.qty)} {d.unit}{d.room ? ` · ${d.room}` : ''}</div>
              <ul style={{ fontSize: 15, lineHeight: 1.6, paddingLeft: 20, margin: '12px 0' }}>
                <li>ยอดนี้จะถูก<b>หักจากใบจ่ายเงินรอบถัดไป</b>ของคุณ</li>
                <li>หักไม่เกิน <b>{cap !== null && cap !== undefined ? `${cap}%` : 'เพดานที่ที่พักกำหนด'}</b> ของยอดงานในแต่ละใบ — ส่วนที่เหลือจะยกไปหักในใบถัดไป</li>
                <li>การกดยอมรับ = คุณยินยอมให้หักค่าจ้างเป็นค่าเสียหายรายการนี้</li>
              </ul>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>เซ็นชื่อ <span style={{ fontWeight: 600, color: '#8A938B', fontSize: 13 }}>(ไม่บังคับ)</span></div>
              <SignaturePad padRef={padRef} onInk={setHasInk} />
              {hasInk && <button style={{ ...S.back, marginTop: 6, color: '#586159' }} onClick={() => padRef.current?.clear()}>ล้างลายเซ็น</button>}
              <FieldErr msg={ackError} />
              <button disabled={busy} style={S.bigBtn('#0F8A56')} onClick={acknowledge}>{busy ? 'กำลังส่ง…' : 'ยืนยัน ยอมรับยอดนี้'}</button>
              <button disabled={busy} style={{ ...S.back, width: '100%', marginTop: 10, padding: 14 }} onClick={() => setAckOpen(false)}>ยกเลิก</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!sme.enabled) {
    return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 40 }}>📦</div><p style={{ marginTop: 12, color: '#586159' }}>ที่พักนี้ยังไม่เปิดใช้ระบบคลังสินค้า</p>
      {onBack && <button style={{ ...S.back, marginTop: 12 }} onClick={onBack}>‹ กลับหน้าหลัก</button>}
    </div>;
  }

  // ================= TABS =================
  const tabBtn = (on) => ({ position: 'relative', flex: '1 1 40%', border: 'none', borderRadius: 10, padding: '12px 4px', fontWeight: 800, fontSize: 14, background: on ? '#0F8A56' : '#EEF2EF', color: on ? '#fff' : '#586159' });
  const pending = Number(sme.pending_ack) || 0;
  const roomName = (id) => rooms.find((r) => String(r.id) === String(id))?.name || '';

  const listView = (
    <>
      {list === null && <div style={{ textAlign: 'center', color: '#8A938B', padding: 20 }}>กำลังโหลด…</div>}
      {listError && <div style={{ ...S.card, textAlign: 'center', color: '#CD3E33' }}>{listError}</div>}
      {list && !listError && list.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>{tab === 'mine' ? 'ไม่มีรายการหัก 🎉' : 'ยังไม่มีรายการที่แจ้งใน 60 วัน'}</div>}
      {tab === 'mine' && (list || []).some((i) => i.status === 'awaiting_ack') && (
        <div style={{ fontSize: 14, color: '#586159', marginBottom: 8 }}>รายการ "รอแม่บ้านยอมรับ" ต้องให้คุณกดยอมรับหรือโต้แย้งก่อน จะไม่มีการหักเงินโดยที่คุณไม่ได้กดยอมรับ</div>
      )}
      {(list || []).slice().sort((a, b) => (tab === 'mine' ? (b.status === 'awaiting_ack') - (a.status === 'awaiting_ack') : 0)).map((i) => (
        <button key={i.id} onClick={() => openIncident(i.id)} style={{ ...S.card, display: 'block', width: '100%', textAlign: 'left', borderLeft: `5px solid ${STATUS_COLOR[i.status] || '#DBE2DC'}` }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {thumb((i.photos || [])[0] || i.photo_url)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>{i.name} × {Number(i.qty)} {i.unit}</div>
              <div style={{ fontSize: 13, color: '#586159' }}>{i.type_label}{i.room ? ` · ${i.room}` : ''} · {i.occurred_at ? moment(i.occurred_at).format('D MMM') : ''}{i.incident_no ? ` · ${i.incident_no}` : ''}</div>
              {i.is_mine && Number(i.charge_amount) > 0 && <div style={{ fontSize: 14, fontWeight: 800, color: '#CD3E33', marginTop: 2 }}>฿{money(i.charge_amount)}</div>}
            </div>
            <div style={{ flexShrink: 0 }}>{pill(i)}</div>
          </div>
        </button>
      ))}
    </>
  );

  // ---------- ฟอร์มแจ้ง ----------
  const q = itemQ.trim().toLowerCase();
  const inRoom = (items || []).filter((x) => x.in_room && (!q || String(x.name).toLowerCase().includes(q)));
  const others = (items || []).filter((x) => !x.in_room);
  const othersShown = q ? others : others.slice(0, 30);
  const itemRow = (x) => (
    <button key={x.id} onClick={() => editF({ item_id: x.id, item_label: x.name, custom: false, name: '', item_photo: x.photo_url || null, item_unit: x.unit || '' })}
      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', border: 'none', borderTop: '1px solid #EEF1EE', background: 'none', padding: '10px 0', minHeight: 56 }}>
      {thumb(x.photo_url, 44)}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{x.name}</div>
        {x.in_room && <div style={{ fontSize: 13, color: Number(x.short) > 0 ? '#B26B0E' : '#586159' }}>ควรมี {Number(x.par_qty)} · ในห้อง {Number(x.qty_in_room)} {x.unit || ''}</div>}
      </div>
      <span style={{ color: '#2C6DAF', fontWeight: 800 }}>เลือก</span>
    </button>
  );

  const reportView = created ? (
    <>
      <div style={{ ...S.card, background: '#F1F7F3', borderColor: '#0F8A56', textAlign: 'center' }}>
        <div style={{ fontSize: 40 }}>✅</div>
        <div style={{ fontSize: 18, fontWeight: 800, marginTop: 6 }}>ส่งเรื่องแล้ว {created.incident_no || ''}</div>
        <div style={{ fontSize: 15, marginTop: 6 }}>{created.name} × {Number(created.qty)} {created.unit} · {created.type_label}{created.room ? ` · ${created.room}` : ''}</div>
        <div style={{ marginTop: 8 }}>{pill(created)}</div>
      </div>
      <button style={S.bigBtn('#0F8A56')} onClick={() => setCreated(null)}>+ แจ้งรายการอื่น</button>
      <button style={{ ...S.back, width: '100%', marginTop: 10, padding: 14 }} onClick={() => switchTab('reported')}>ดูรายการที่ฉันแจ้ง</button>
    </>
  ) : (
    <div style={S.card}>
      <div style={label}>ห้อง<Req /></div>
      <select style={{ ...input, ...errBorder('room_id') }} value={f.room_id || ''} onChange={(e) => editF({ room_id: e.target.value, item_id: null, item_label: '' })}>
        <option value="">— เลือกห้อง —</option>
        {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      <FieldErr msg={errors.room_id} />

      <div style={{ ...label, marginTop: 16 }}>ของอะไร<Req /></div>
      {f.item_id && !f.custom && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, borderRadius: 12, background: '#F1F7F3', border: '1.5px solid #0F8A56' }}>
          {thumb(f.item_photo, 48)}
          <div style={{ flex: 1, fontWeight: 800, fontSize: 16 }}>{f.item_label}</div>
          <button style={{ ...S.back, color: '#586159' }} onClick={() => editF({ item_id: null, item_label: '' })}>เปลี่ยน</button>
        </div>
      )}
      {f.custom && (
        <>
          <input value={f.name} onChange={(e) => editF({ name: e.target.value })} placeholder="พิมพ์ชื่อของ เช่น ไม้แขวนเสื้อ" style={{ ...input, ...errBorder('item_id') }} />
          <button style={{ ...S.back, marginTop: 8, color: '#2C6DAF' }} onClick={() => editF({ custom: false, name: '' })}>‹ เลือกจากรายการแทน</button>
        </>
      )}
      {!f.item_id && !f.custom && (
        <div style={{ border: `1.5px solid ${errors.item_id ? '#CD3E33' : '#DBE2DC'}`, borderRadius: 12, padding: 10 }}>
          <input value={itemQ} onChange={(e) => setItemQ(e.target.value)} placeholder="🔍 ค้นหาของ" style={input} />
          {items === null && <div style={{ color: '#8A938B', padding: 10 }}>กำลังโหลด…</div>}
          {inRoom.length > 0 && <div style={{ fontSize: 13, fontWeight: 800, color: '#0F8A56', marginTop: 10 }}>ของประจำห้อง{f.room_id ? ` ${roomName(f.room_id)}` : ''}</div>}
          {inRoom.map(itemRow)}
          {othersShown.length > 0 && <div style={{ fontSize: 13, fontWeight: 800, color: '#586159', marginTop: 10 }}>ของอื่นในคลังสินค้า</div>}
          {othersShown.map(itemRow)}
          {!q && others.length > othersShown.length && <div style={{ fontSize: 13, color: '#8A938B', padding: '6px 0' }}>มีอีก {others.length - othersShown.length} รายการ — พิมพ์ค้นหา</div>}
          {items && !inRoom.length && !othersShown.length && <div style={{ color: '#8A938B', padding: 10 }}>ไม่พบ{q ? ` "${itemQ}"` : 'ของในรายการ'}</div>}
          <button style={{ ...S.back, width: '100%', marginTop: 10, padding: 12, color: '#2C6DAF' }} onClick={() => editF({ custom: true, item_id: null, item_label: '', name: itemQ.trim() })}>+ ของไม่มีในรายการ (พิมพ์ชื่อเอง)</button>
        </div>
      )}
      <FieldErr msg={errors.item_id} />

      <div style={{ ...label, marginTop: 16 }}>จำนวน<Req /></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button style={{ ...S.back, fontSize: 22, padding: '8px 20px' }} onClick={() => editF({ qty: String(Math.max(1, (Number(f.qty) || 0) - 1)) })}>−</button>
        <input type="number" inputMode="decimal" min="0" value={f.qty} onChange={(e) => editF({ qty: e.target.value })}
          style={{ ...input, textAlign: 'center', fontSize: 22, fontWeight: 800, ...errBorder('qty') }} />
        <button style={{ ...S.back, fontSize: 22, padding: '8px 20px' }} onClick={() => editF({ qty: String((Number(f.qty) || 0) + 1) })}>+</button>
      </div>
      <FieldErr msg={errors.qty} />

      <div style={{ ...label, marginTop: 16 }}>เกิดอะไรขึ้น<Req /></div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {INCIDENT_TYPES.map((t) => <button key={t.v} style={chip(f.incident_type === t.v, '#B26B0E')} onClick={() => editF({ incident_type: t.v })}>{t.label}</button>)}
      </div>
      <FieldErr msg={errors.incident_type} />

      <div style={{ ...label, marginTop: 16 }}>เกิดจากใคร<Req /></div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {RESPONSIBLE.map((t) => <button key={t.v} style={chip(f.responsible === t.v, '#2C6DAF')} onClick={() => editF({ responsible: t.v })}>{t.label}</button>)}
      </div>
      <FieldErr msg={errors.responsible} />
      {f.responsible === 'self' && (
        <div style={{ marginTop: 10, padding: 12, borderRadius: 12, background: '#F1F7F3', border: '1px solid #CFE6DA', fontSize: 15, lineHeight: 1.5 }}>
          🙏 แจ้งตามจริง — แอดมินจะพิจารณา ถ้าเป็นอุบัติเหตุ/ใช้งานปกติจะไม่หักเงิน และจะไม่มีการหักเงินโดยที่คุณไม่ได้กดยอมรับ
        </div>
      )}

      <div style={{ ...label, marginTop: 16 }}>รูป <span style={{ fontWeight: 600, color: '#8A938B', fontSize: 13 }}>({f.photos.length}/{MAX_PHOTOS})</span></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {f.photos.map((u) => (
          <div key={u} style={{ position: 'relative' }}>
            <img src={u} alt="" style={{ display: 'block', width: '100%', height: 92, objectFit: 'cover', borderRadius: 10 }} />
            <button onClick={() => editF({ photos: f.photos.filter((x) => x !== u) })}
              style={{ position: 'absolute', top: 4, right: 4, width: 32, height: 32, borderRadius: 16, border: 'none', background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 16, fontWeight: 800, lineHeight: '32px', padding: 0 }}>✕</button>
          </div>
        ))}
        {f.photos.length + uploading < MAX_PHOTOS && (
          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 92, border: '2px dashed #CBD4CC', borderRadius: 10, fontSize: 28 }}>
            📷
            <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => { onPickPhotos(e.target.files); e.target.value = ''; }} />
          </label>
        )}
        {uploading > 0 && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 92, fontSize: 13, color: '#586159' }}>⏳ กำลังส่ง {uploading} รูป</div>}
      </div>
      <FieldErr msg={errors.photos} />

      <div style={{ ...label, marginTop: 16 }}>โน้ต</div>
      <textarea rows={3} value={f.note} onChange={(e) => editF({ note: e.target.value })} placeholder="เช่น เจอแก้วแตกในอ่างล้างจาน" style={input} />

      <FieldErr msg={errors.submit} />
      <button disabled={busy || uploading > 0} style={S.bigBtn(uploading ? '#8A938B' : '#CD3E33')} onClick={submitReport}>
        {busy ? 'กำลังส่ง…' : uploading ? '⏳ รอรูปส่งเสร็จ…' : 'ส่งเรื่องให้แอดมิน'}
      </button>
      <div style={{ fontSize: 12, color: '#8A938B', marginTop: 8, textAlign: 'center' }}>ร่างจะเก็บไว้ในเครื่องจนกว่าจะส่งสำเร็จ</div>
    </div>
  );

  // ---------- ของในห้อง ----------
  const shortRows = (parRows || []).filter((r) => Number(r.short) > 0);
  const roomView = (
    <div style={S.card}>
      <div style={label}>ห้อง</div>
      <select style={input} value={parRoom} onChange={(e) => loadPars(e.target.value)}>
        <option value="">— เลือกห้อง —</option>
        {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      {parRoom && parRows === null && <div style={{ color: '#8A938B', padding: 10 }}>กำลังโหลด…</div>}
      {parRows && parRows.length === 0 && !parError && <div style={{ color: '#8A938B', padding: '10px 0' }}>ห้องนี้ยังไม่ได้ตั้งของประจำห้อง</div>}
      {(parRows || []).map((r) => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid #EEF1EE' }}>
          {thumb(r.photo_url, 44)}
          <div style={{ flex: 1, minWidth: 0, fontWeight: 700 }}>{r.name}</div>
          <div style={{ textAlign: 'right', fontSize: 14 }}>
            <div>ในห้อง <b>{Number(r.qty_in_room)}</b> / {Number(r.par_qty)}</div>
            {Number(r.short) > 0 && <div style={{ color: '#B26B0E', fontWeight: 800 }}>ขาด {Number(r.short)}</div>}
          </div>
        </div>
      ))}
      {fillResult && (
        <div style={{ marginTop: 10, padding: 12, borderRadius: 12, background: '#F1F7F3', border: '1px solid #CFE6DA', fontSize: 14 }}>
          {fillResult.length === 0 && <div style={{ fontWeight: 800 }}>ของในห้องครบแล้ว ✓</div>}
          {fillResult.map((x) => (
            <div key={x.item_id}>
              {Number(x.qty) > 0 ? `✓ เติม ${x.name} ${Number(x.qty)}` : `✗ ${x.name}: คลังไม่มีของ`}
              {Number(x.short) > 0 && <span style={{ color: '#B26B0E', fontWeight: 700 }}> · ยังขาด {Number(x.short)} (แจ้งแอดมิน)</span>}
            </div>
          ))}
        </div>
      )}
      {parError && <FieldErr msg={parError} />}
      {parRoom && shortRows.length > 0 && (
        <button disabled={busy} style={S.bigBtn('#0F8A56')} onClick={fillRoom}>{busy ? 'กำลังบันทึก…' : 'เติมของเข้าห้องให้ครบ'}</button>
      )}
      {parRoom && shortRows.length > 0 && <div style={{ fontSize: 12, color: '#8A938B', marginTop: 6, textAlign: 'center' }}>ระบบย้ายของจากคลังหลักเข้าห้องเท่าที่ขาด — กดหลังเอาของเข้าห้องจริงแล้ว</div>}
    </div>
  );

  return (
    <div style={S.page}>
      {header('📦 ของเสีย/หาย · คลังสินค้า', null, onBack, '‹ หน้าหลัก')}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {TABS.map(([k, l]) => (
          <button key={k} style={tabBtn(tab === k)} onClick={() => switchTab(k)}>
            {l}
            {k === 'mine' && pending > 0 && <span style={{ marginLeft: 6, display: 'inline-block', minWidth: 20, padding: '1px 6px', borderRadius: 99, background: '#CD3E33', color: '#fff', fontSize: 12 }}>{pending}</span>}
          </button>
        ))}
      </div>
      {tab === 'report' && reportView}
      {(tab === 'reported' || tab === 'mine') && listView}
      {tab === 'room' && roomView}
    </div>
  );
}
